/**
 * 客户端菜单模块（单一权威来源）
 *
 * 菜单有三层来源：
 *  1. 后端 WebMenu.php（登录时返回）——结构见 App\Constants\WebMenu
 *  2. 客户端兜底 CLIENT_MENU（后端 Hyperf 未重启 / 旧缓存时使用，与本仓库后端保持一致）
 *  3. localStorage 缓存（登录成功时写入，刷新时恢复）
 *
 * 为保证「登录后菜单 == 刷新后菜单」，所有菜单生成/校验逻辑统一收口到这里，
 * store 只负责调用，不再各自内联逻辑。
 */

/**
 * 客户端权威菜单（两级结构，与后端 App\Constants\WebMenu.php 保持一致）。
 * 父级项：{ url, name, icon, isParent: true, children: [{ url, name, icon }] }
 * 顶级项：{ url, name, icon }
 */
export const CLIENT_MENU = [
  { url: 'home', name: '工作台', icon: 'el-icon-s-home' },
  {
    url: 'factory', name: '厂区设备', icon: 'el-icon-office-building', isParent: true,
    children: [
      { url: 'factory', name: '厂区管理', icon: 'el-icon-office-building' },
      { url: 'device',  name: '设备管理', icon: 'el-icon-truck' },
      { url: 'asset',   name: '设备资产', icon: 'el-icon-coin' }
    ]
  },
  {
    url: 'monitor', name: '实时监控', icon: 'el-icon-location', isParent: true,
    children: [
      { url: 'monitor',  name: '实时监控', icon: 'el-icon-location' },
      { url: 'track',    name: '轨迹回放', icon: 'el-icon-position' },
      { url: 'behavior', name: '行为分析', icon: 'el-icon-data-line' }
    ]
  },
  {
    url: 'security', name: '安全与告警', icon: 'el-icon-warning', isParent: true,
    children: [
      { url: 'security', name: '安全管理', icon: 'el-icon-warning' },
      { url: 'alert',    name: '告警中心', icon: 'el-icon-bell' }
    ]
  },
  {
    url: 'data-center', name: '数据中心', icon: 'el-icon-data-analysis', isParent: true,
    children: [
      { url: 'data-center', name: '数据中心', icon: 'el-icon-data-analysis' },
      { url: 'report',      name: '报表中心', icon: 'el-icon-s-data' }
    ]
  },
  {
    url: 'workorder', name: '运营管理', icon: 'el-icon-s-order', isParent: true,
    children: [
      { url: 'workorder', name: '运维工单', icon: 'el-icon-s-order' },
      { url: 'audit',     name: '审计日志', icon: 'el-icon-document' },
      { url: 'message',   name: '消息中心', icon: 'el-icon-chat-dot-round' }
    ]
  },
  { url: 'mine', name: '账号管理', icon: 'el-icon-user' }
]

/** 合法父级 url 集合（用于校验缓存/后端菜单是否含废弃父级） */
const VALID_PARENT_PATHS = new Set(
  CLIENT_MENU.filter(m => m.isParent).map(m => '/' + m.url)
)

/**
 * 将菜单定义（后端原始结构或 CLIENT_MENU）转换为前端 menuList 格式：
 * { path, name, icon, isParent, children }
 */
export function buildMenuList(menuDef) {
  return (menuDef || []).map(item => ({
    path: `/${item.url || item.menu_id}`,
    name: item.name,
    icon: item.icon,
    isParent: Array.isArray(item.children) && item.children.length > 0,
    children: item.children || []
  }))
}

/** 判断一份菜单定义是否为「合法两级」：至少有一个父级，且所有父级 url 都在 CLIENT_MENU 合法集合内 */
export function menuIsValidTwoLevel(menuDef) {
  const list = menuDef || []
  if (!list.length) return false
  const hasAnyParent = list.some(m => Array.isArray(m.children) && m.children.length > 0)
  if (!hasAnyParent) return false // 全平铺 = 旧版，不合法
  return list.every(m => {
    if (!(Array.isArray(m.children) && m.children.length > 0)) return true // 顶级项不管
    return VALID_PARENT_PATHS.has('/' + (m.url || ''))
  })
}

/**
 * 判断一份 localStorage 恢复的 menuList（前端格式）是否为「合法两级」：
 * 至少有一个 isParent 项，且所有父级 path 都在合法集合内。
 * 用于 initUserInfo：旧缓存（全平铺 / 含废弃父级如「系统管理」）→ 重建为 CLIENT_MENU。
 */
export function menuListIsValid(menuList) {
  const list = menuList || []
  const hasAnyParent = list.some(m => m && m.isParent)
  if (!hasAnyParent) return false // 全平铺 = 旧版缓存
  return list.every(m => !m.isParent || VALID_PARENT_PATHS.has(m.path))
}

/** 客户端兜底 menuList（两级） */
export function buildFallbackMenuList() {
  return buildMenuList(CLIENT_MENU)
}
